import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';

/**
 * Was auf der aktuellen Seite markiert ist – so, wie es mit jeder Chat-Nachricht ans Backend geht
 * (`ChatContext.PageContext`). Die Route sagt nur, WELCHE Seite offen ist; hier steht, WORAUF der
 * Nutzer dort schaut: die markierten Dateien im Dateimanager, Gruppe und Regel im Bericht.
 */
export interface ChatPageContext {
  kind: 'files' | 'report';
  selectedFileIds?: string[];
  reportId?: string;
  reportType?: string;
  groupName?: string;
  ruleName?: string;
  ruleId?: string;
}

/**
 * Sammelstelle für den Seitenzustand. Seiten melden ihn mit {@link set} und räumen ihn mit
 * {@link clear} wieder ab; der Chat liest ihn beim Senden mit {@link snapshot}.
 *
 * <p>Jeder Eintrag gehört einem Besitzer (der Seite) und einem Routen-Präfix. Verlässt die Route das
 * Präfix, gilt der Eintrag nicht mehr – auch dann, wenn die Seite ihn beim Zerstören nicht abräumt.
 * Ohne das hinge eine Dateiauswahl noch an Nachrichten, die auf dem Dashboard geschrieben werden.
 */
@Injectable({providedIn: 'root'})
export class ChatPageContextService {
  private entry?: { owner: string; routePrefix: string; context: ChatPageContext };
  private readonly dataChanges = new Subject<'files'>();

  /**
   * Der Chat hat Daten geändert, die eine Seite zeigt (Signal `filesChanged` am Ende des Turns).
   * Der Dateimanager hat keinen eigenen Live-Kanal dafür und lädt sonst erst beim nächsten Betreten.
   */
  readonly dataChanged$: Observable<'files'> = this.dataChanges.asObservable();

  notifyDataChanged(kind: 'files'): void {
    this.dataChanges.next(kind);
  }

  /** Setzt den Zustand einer Seite. Leere Auswahl und leerer Bericht räumen ihn ab. */
  set(owner: string, routePrefix: string, context: ChatPageContext): void {
    const empty = !(context.selectedFileIds?.length) && !context.groupName && !context.ruleName;
    if (empty) {
      this.clear(owner);
      return;
    }
    this.entry = {owner, routePrefix, context};
  }

  /** Räumt den Zustand ab, aber nur den eigenen – eine neue Seite kann ihn schon überschrieben haben. */
  clear(owner: string): void {
    if (this.entry?.owner === owner) this.entry = undefined;
  }

  /** Der Zustand für die gerade offene Route, oder `undefined`. */
  snapshot(currentRoute: string): ChatPageContext | undefined {
    if (!this.entry) return undefined;
    const path = currentRoute.split(/[?#]/)[0];
    if (!path.startsWith(this.entry.routePrefix)) {
      this.entry = undefined;
      return undefined;
    }
    return this.entry.context;
  }
}
